import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';

export type IInsightsLens = 'focus' | 'value';

const LENSES: { value: IInsightsLens; label: string }[] = [
    { value: 'focus', label: 'Dashboard' },
    { value: 'value', label: 'Trends' }
];

const useStyles = createUseStyles({
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'wrap' as const
    },

    tabs: {
        display: 'inline-flex',
        padding: 3,
        borderRadius: 9,
        background: BRAND.tealWash,
        border: `1px solid ${BRAND.tealEdge}`
    },

    tab: {
        border: 0,
        borderRadius: 7,
        padding: '7px 17px',
        font: 'inherit',
        fontSize: 13.5,
        color: BRAND.deepTeal,
        background: 'none',
        cursor: 'pointer'
    },

    tabOn: {
        background: 'linear-gradient(180deg, var(--ion-color-primary) 0%, var(--ion-color-primary-shade) 100%)',
        color: '#fff',
        fontWeight: 500
    },

    blurb: {
        fontSize: 12.5,
        color: CHART_INK.label
    }
});

/**
 * The two readers of one digest, and a line saying which question each answers.
 *
 * The blurb is not decoration: the same numbers mean different things to a dispatcher and to
 * leadership, and naming the question is what stops one of them reading the other's page as theirs.
 */
export const InsightsLensSwitch: FC<{
    lens: IInsightsLens;
    windowWeeks: number;
    onChange: (lens: IInsightsLens) => void;
}> = ({ lens, windowWeeks, onChange }) => {
    const classes = useStyles();

    return (
        <div className={classes.row}>
            <div className={classes.tabs} role="tablist" aria-label="Insights lens">
                {LENSES.map(({ value, label }) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={lens === value}
                        data-id={`insights-lens-${value}`}
                        className={`${classes.tab} ${lens === value ? classes.tabOn : ''}`}
                        onClick={() => onChange(value)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <span className={classes.blurb}>
                {lens === 'focus'
                    ? 'Who to see today, judged against each client\'s own normal.'
                    : `Outcomes and family participation this week, against activity over the last ${windowWeeks} weeks.`}
            </span>
        </div>
    );
};
